export type PropertyValue = string | string[]
export const BOOLEAN_PROPERTY_ALLOWED_VALUES = ['true', 'false']
export type PropertyValuesRecord = Record<string, PropertyValue>

export type OrgEditPermissions = 'all' | 'definitions' | 'values'
export type PropertyValidationErrors = Record<string, string>
export type ValueType = 'string' | 'single_select' | 'multi_select' | 'true_false' | 'url'
export type ValuesEditableBy = 'org_and_repo_actors' | 'org_actors'

export type SourceType = 'org' | 'business'

export interface PropertyDefinitionBase {
  propertyName: string
  valueType: ValueType
  required: boolean
  defaultValue: PropertyValue | null
  description: string | null
  allowedValues: string[] | null
  regex: string | null
  source: PropertySourceDetails
}

export interface PropertyDefinition extends PropertyDefinitionBase {
  valuesEditableBy: ValuesEditableBy
  requireExplicitValues: boolean
}

export type PropertyType = 'custom' | 'external'

export type FilterDefinition = {
  propertyName: string
  valueType: ValueType
  required?: boolean
  allowedValues?: string[] | null
}

export type PropertyNamespace = {
  namespace: string
  type: PropertyType
  definitions: FilterDefinition[]
}

export interface Repository {
  id: number
  name: string
  visibility: 'public' | 'private' | 'internal'
  description: string | null
  properties?: PropertyValuesRecord
}

export interface PropertySourceDetails extends SourceInfo {
  type: SourceType
  avatarUrl: string
}

interface DefinitionsListResults {
  definitions: PropertyDefinition[]
  pageCount: number
  totalCount: number
}

export interface RepositoryListResults {
  repositories: Repository[]
  repositoryCount: number
  pageCount: number
}

export interface BusinessCustomPropertiesDefinitionsPagePayload extends DefinitionsListResults {
  ownDefinitionsCount: number
  currentQ: string
}

export interface OrgCustomPropertiesListPagePayload extends DefinitionsListResults {
  ownDefinitionsCount: number
  permissions: OrgEditPermissions
  activeTab: 'properties'
}

export interface OrgCustomPropertiesSetValuesPagePayload extends RepositoryListResults {
  ownDefinitionsCount: number
  definitions: PropertyDefinition[]
  permissions: OrgEditPermissions
  activeTab: 'set-values'
  business?: SourceInfo
  propertiesLimits: PropertiesLimits
  namespaces?: PropertyNamespace[]
}

export type OrgCustomPropertiesPagePayload =
  | OrgCustomPropertiesListPagePayload
  | OrgCustomPropertiesSetValuesPagePayload

export type PropertiesPageTabName = OrgCustomPropertiesPagePayload['activeTab']

export interface SourceInfo {
  name: string
  slug: string
}

export interface RepoPropertiesPagePayload {
  definitions: PropertyDefinition[]
  values: PropertyValuesRecord
  canEditProperties: boolean
}

export interface RepoSettingsPropertiesPagePayload {
  definitions: PropertyDefinition[]
  currentRepo: Repository
  editableProperties: string[]
  propertiesLimits: PropertiesLimits
}

export interface CustomPropertyDetailsPagePayload {
  definition?: PropertyDefinition
  propertyNames: string[]
  business?: SourceInfo
  canManageProperty: boolean
  orgConflicts?: PropertyNameOrgConflicts
  propertiesLimits: PropertiesLimits
  /** Absent on the enterprise view of an organization-owned property, which is read-only. */
  copilotPropertySuggestionsEnabled?: boolean
}

export interface CheckPropertyUsagesResponse {
  repositoriesCount: number
}

export interface PropertyNameValidationResult {
  message: string
  orgConflicts?: PropertyNameOrgConflicts
}

export interface PropertyNameOrgConflicts {
  usages: OrgConflictUsage[]
  totalUsageCount: number
}

export interface OrgConflictUsage {
  name: string
  avatarUrl: string
  propertyType: ValueType
}

export interface TargetLiterals {
  single: string
  multiple: string
}

export interface PropertyTarget {
  id: number
  properties?: PropertyValuesRecord
}

export interface PropertyUsagesResponse {
  count: number
  resourceUsages?: string[]
}

export type BannerType =
  | 'definition.created.success'
  | 'definition.updated.success'
  | 'definition.deleted.success'
  | 'definition.promotion.success'
  | 'values.updated.success'

export interface PropertiesLimits {
  maxPropertyValueLength: number
  maxUrlPropertyValueLength: number
}

export type ResourceConfig = {
  /**
   * A user-facing display name for the resource that may be using a property.
   */
  displayName: string
  /**
   * Builds the URL to this resource's settings page. When provided, the display
   * name is rendered as a link; when omitted it is rendered as plain text.
   *
   * @param urlPrefix - The org or enterprise path prefix derived from the
   *   current page URL (e.g. `"organizations/my-org"` or `"enterprises/my-enterprise"`).
   */
  resourcePath?: (urlPrefix: string) => string
}
